import TripList from "../../Trips/TripsList";
import { useGetTripsQuery } from "../../services";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QIErrorBoundary } from "../../../../components";
import moment from "moment";
import "./style.scss";
import { header } from "../../Trips/tableHeader";
import { getLocalizedString } from "../../../../shared/translation";

export const TripsContainer = ({
  deviceId,
  clientId,
  selectedRange,
  resetCount,
  setResetCount,
  setSelectedRange,
  selectedVehiclePlate,
  type,
  setActivitiesTimeRange = () => {},
  setListCount = () => {},
  setMapSelectionLoading = () => {},
}) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [tripSearch, setTripSearch] = useState("");
  const [selectedId, setSelectedId] = useState();
  const [isSelectionChanging, setIsSelectionChanging] = useState(false);

  const [tripsList, setTripsList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const processedPagesRef = useRef(new Set());
  const loadMoreLockedRef = useRef(false);

  const rangeStart = useMemo(
    () => moment(selectedRange.startDate).valueOf(),
    [selectedRange.startDate]
  );
  const rangeEnd = useMemo(() => moment(selectedRange.endDate).valueOf(), [selectedRange.endDate]);

  // Context uniquely identifies the current data set. Changing it resets the
  // accumulated rows so pages from a previous selection are never mixed in.
  const dataContext = `${type}:${deviceId || ""}:${selectedVehiclePlate || ""}:${rangeStart}:${rangeEnd}:${perPage}`;

  useEffect(() => {
    setTripsList([]);
    setTotalCount(0);
    setPage(1);
    setSelectedId(undefined);
    processedPagesRef.current.clear();
    loadMoreLockedRef.current = false;
  }, [dataContext]);

  const message = {
    header: getLocalizedString("no_device_selected", ""),
    content: getLocalizedString("please_select_a_device", "Please Select a Device"),
  };

  //Get Trips data
  const {
    data: tripsData,
    error,
    isFetching,
    isSuccess,
  } = useGetTripsQuery(
    deviceId && {
      startTime: rangeStart,
      endTime: rangeEnd,
      deviceId,
      clientId: clientId,
      plate_number: [selectedVehiclePlate],
      source_id: [deviceId],
      type: type,
      search: false,
      page: page - 1,
      per_page: perPage,
      q: "",
    }
  );

  // Get end time if present for past trip or return current time for in progress trip
  const getEndTime = (endTime) => {
    if (endTime !== null) {
      return moment(endTime);
    } else {
      return moment();
    }
  };

  // Enhanced setActivitiesTimeRange that triggers loading state
  const handleActivitiesTimeRangeChange = (timeRange) => {
    setIsSelectionChanging(true);
    setMapSelectionLoading(true);
    setActivitiesTimeRange(timeRange);

    // Reset loading state after a short delay to allow map to update
    setTimeout(() => {
      setIsSelectionChanging(false);
      setMapSelectionLoading(false);
    }, 500);
  };

  // Accumulate pages instead of replacing. Each page is processed once, guarded
  // by processedPagesRef so React re-renders cannot duplicate rows.
  useEffect(() => {
    if (!tripsData || !isSuccess || isFetching || !deviceId) return;

    loadMoreLockedRef.current = false;
    setTotalCount(tripsData.total_count || 0);

    const processedPageKey = `${dataContext}:${page}`;
    if (processedPagesRef.current.has(processedPageKey)) return;
    processedPagesRef.current.add(processedPageKey);

    if (tripsData?.trips?.length) {
      setTripsList((previousRows) => [...previousRows, ...tripsData.trips]);
    }
  }, [tripsData, isSuccess, isFetching, deviceId, dataContext, page]);

  useEffect(() => {
    if (page === 1 && tripsData?.trips?.length > 0) {
      setActivitiesTimeRange({
        start_time: moment(tripsData?.trips[0]?.start_time),
        end_time: getEndTime(tripsData?.trips[0]?.end_time),
      });
      setSelectedId(0);
    }
  }, [tripsData?.trips, page]);

  useEffect(() => {
    if (error) {
      loadMoreLockedRef.current = false;
    }
  }, [error]);

  //To set the total count of data in the list
  useEffect(() => {
    setListCount(tripsData?.total_count);
  }, [tripsData?.total_count]);

  const hasMore = tripsList.length < totalCount;

  const handleLoadMore = useCallback(() => {
    if (loadMoreLockedRef.current || isFetching || !hasMore) return;
    loadMoreLockedRef.current = true;
    setPage((currentPage) => currentPage + 1);
  }, [hasMore, isFetching]);

  const handlePerPageChange = useCallback((nextPerPage) => {
    setPerPage(nextPerPage);
  }, []);

  return deviceId ? (
    <TripList
      isFetching={isFetching && tripsList.length === 0}
      isSuccess={isSuccess}
      data={{ trips: tripsList, total_count: totalCount }}
      header={header}
      resetCount={resetCount}
      setResetCount={setResetCount}
      selectedRange={selectedRange}
      setSelectedRange={setSelectedRange}
      error={error}
      listType={"Trips"}
      selectedPage={"Trips"}
      simpleSearch={tripSearch}
      setSimpleSearch={setTripSearch}
      batchSize={perPage}
      onBatchSizeChange={handlePerPageChange}
      hasMore={hasMore}
      isLoadingMore={isFetching}
      onLoadMore={handleLoadMore}
      setActivitiesTimeRange={handleActivitiesTimeRangeChange}
      selectedId={selectedId}
    />
  ) : (
    <QIErrorBoundary message={message} />
  );
};
